import { useSeo } from "@/hooks/useSeo";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle, CheckCircle, Instagram, Mail, Send } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const subjectOptions = [
  { value: "general", label: "General Enquiry", requiresOrder: false },
  { value: "product-info", label: "Product Information", requiresOrder: false },
  { value: "order-issue", label: "Issue with My Order", requiresOrder: true },
  { value: "returns", label: "Returns & Refunds", requiresOrder: true },
  { value: "missing-item", label: "Missing / Damaged Item", requiresOrder: true },
  { value: "business", label: "Business Enquiry", requiresOrder: false },
  { value: "other", label: "Other", requiresOrder: false },
] as const;

const subjectsThatRequireOrder = new Set(
  subjectOptions.filter((option) => option.requiresOrder).map((option) => option.value),
);

const contactSchema = z
  .object({
    name: z.string().trim().min(1, "Name is required").max(100),
    email: z.string().trim().email("Please enter a valid email").max(255),
    subject: z.enum(["general", "product-info", "order-issue", "returns", "missing-item", "business", "other"], {
      errorMap: () => ({ message: "Please select a subject" }),
    }),
    orderNumber: z.string().trim().max(100).optional(),
    message: z.string().trim().min(1, "Message is required").max(2000),
    consent: z.boolean().refine((value) => value, {
      message: "Please tick the consent box to send your message.",
    }),
  })
  .superRefine((data, ctx) => {
    if (subjectsThatRequireOrder.has(data.subject) && !data.orderNumber?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Order number is required for this subject.",
        path: ["orderNumber"],
      });
    }
  });

type ContactFormData = z.infer<typeof contactSchema>;

const ContactPage = () => {
  useSeo('Contact Us | Luxe Lords', 'Get in touch with Luxe Lords about orders, products, delivery or returns.', '/contact');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [delivered, setDelivered] = useState(false);

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      name: "",
      email: "",
      subject: "general",
      orderNumber: "",
      message: "",
      consent: false,
    },
  });

  const selectedSubject = form.watch("subject");

  const requiresOrderNumber = useMemo(
    () => subjectsThatRequireOrder.has(selectedSubject),
    [selectedSubject],
  );

  useEffect(() => {
    if (!requiresOrderNumber && form.getValues("orderNumber")) {
      form.setValue("orderNumber", "", { shouldValidate: true, shouldDirty: true });
    }
  }, [requiresOrderNumber, form]);

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    setFormStatus("idle");

    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const messageId = crypto.randomUUID();
      const { error } = await supabase.from("contact_messages").insert({
        id: messageId,
        name: data.name,
        email: data.email,
        subject: data.subject,
        order_number: data.orderNumber?.trim() || null,
        message: data.message,
        consent: data.consent,
      });

      if (error) throw error;

      // Try to deliver the team notification email. Only report "sent" if delivery succeeds.
      let sent = false;
      try {
        const { data: sendData, error: sendError } = await supabase.functions.invoke("contact-notify", {
          body: { messageId },
        });
        sent = !sendError && sendData?.sent === true;
      } catch {
        sent = false;
      }

      setDelivered(sent);
      setFormStatus("success");
      form.reset({
        name: "",
        email: "",
        subject: "general",
        orderNumber: "",
        message: "",
        consent: false,
      });
    } catch {
      setFormStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main className="pt-32 pb-20 px-6 lg:px-12">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-16">
            <p className="text-luxury text-muted-foreground mb-4">Get in Touch</p>
            <h1 className="heading-section text-foreground mb-4">
              We'd Love to <em>Hear From You</em>
            </h1>
            <p className="text-body text-muted-foreground max-w-lg mx-auto">
              Whether you have a question about our collection or want to discuss a bespoke order,
              we're here to help.
            </p>
          </div>

          <div className="rounded-2xl bg-secondary/60 border border-border p-8 md:p-12 lg:p-16">

          <div className="grid md:grid-cols-5 gap-12 lg:gap-16">
            <div className="md:col-span-3">
              {formStatus === "success" ? (
                <div className="rounded-xl border border-primary/20 bg-card p-8 text-center space-y-4">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                    <CheckCircle className="h-7 w-7 text-primary" />
                  </div>
                  {delivered ? (
                    <>
                      <h2 className="text-luxury text-foreground">Message sent</h2>
                      <p className="text-body text-muted-foreground">
                        Thank you for contacting Luxe Lords. Our team has your message and will reply
                        within 24 hours on business days.
                      </p>
                    </>
                  ) : (
                    <>
                      <h2 className="text-luxury text-foreground">Message queued for delivery</h2>
                      <p className="text-body text-muted-foreground">
                        Thank you for contacting Luxe Lords. Your message has been securely logged and is
                        in line to reach our team.
                      </p>
                      <p className="text-sm text-muted-foreground">
                        For a faster response, please email us directly at{" "}
                        <a href="mailto:info@luxelords.com" className="text-foreground underline underline-offset-4">
                          info@luxelords.com
                        </a>
                        .
                      </p>
                    </>
                  )}
                  <Button variant="outline" onClick={() => setFormStatus("idle")}>
                    Send another message
                  </Button>
                </div>
              ) : formStatus === "error" ? (
                <div className="rounded-xl border border-destructive/20 bg-card p-8 text-center space-y-4">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
                    <AlertTriangle className="h-7 w-7 text-destructive" />
                  </div>
                  <h2 className="text-luxury text-foreground">Something went wrong</h2>
                  <p className="text-body text-muted-foreground">
                    Please try again, or email us directly at info@luxelords.com.
                  </p>
                  <Button variant="outline" onClick={() => setFormStatus("idle")}>
                    Try again
                  </Button>
                </div>
              ) : (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" noValidate>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-luxury text-xs">Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Your name" className="bg-card border-border" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-luxury text-xs">Email</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="your@email.com" className="bg-card border-border" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="subject"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-luxury text-xs">Subject</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="bg-card border-border">
                                <SelectValue placeholder="Select a subject" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {subjectOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {requiresOrderNumber && (
                      <FormField
                        control={form.control}
                        name="orderNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-luxury text-xs">Order Number</FormLabel>
                            <FormControl>
                              <Input placeholder="Your order number" className="bg-card border-border" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-luxury text-xs">Message</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Tell us how we can help..."
                              rows={6}
                              className="bg-card border-border resize-none"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="consent"
                      render={({ field }) => (
                        <FormItem>
                          <div className="rounded-lg border border-border p-4 space-y-3">
                            <div className="flex items-start gap-3">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={(checked) => field.onChange(checked === true)}
                                  className="mt-0.5"
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-normal leading-relaxed text-muted-foreground">
                                I consent to Luxe Lords storing my submitted information so the team can respond to my enquiry.
                              </FormLabel>
                            </div>
                            <FormMessage />
                          </div>
                        </FormItem>
                      )}
                    />

                    <Button type="submit" disabled={isSubmitting} className="btn-luxury w-full md:w-auto gap-2">
                      <Send size={16} />
                      {isSubmitting ? "Sending..." : "Send Message"}
                    </Button>
                  </form>
                </Form>
              )}
            </div>

            <div className="md:col-span-2 space-y-10">
              <div>
                <h3 className="text-luxury text-foreground mb-3">Email</h3>
                <a
                  href="mailto:info@luxelords.com"
                  className="text-body text-muted-foreground hover:text-foreground transition-colors"
                >
                  info@luxelords.com
                </a>
              </div>

              <div>
                <h3 className="text-luxury text-foreground mb-3">Follow Us</h3>
                <div className="flex gap-4">
                  <a
                    href="https://www.instagram.com/luxelordsofficial" target="_blank" rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-foreground hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
                    aria-label="Instagram"
                  >
                    <Instagram size={18} />
                  </a>
                  <a
                    href="mailto:info@luxelords.com"
                    className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-foreground hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
                    aria-label="Email"
                  >
                    <Mail size={18} />
                  </a>
                </div>
              </div>

              <div>
                <h3 className="text-luxury text-foreground mb-3">Response Time</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  We typically respond within 24 hours during business days.
                </p>
              </div>
            </div>
          </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;

